import { DocumentType } from '@massilia/prisma-generated/enums';
import { z } from 'zod';
import { AGENCY_SENDER_EMAILS } from '../helpers/agency.js';
/**
 * Destinataires d'un mail de dossier.
 *
 * Le champ « Destinataires » du backoffice est une saisie libre séparée par des
 * virgules, pré-remplie avec `booking.client.email`. Sans validation, une valeur
 * bidon (« nc », un nom, une adresse tronquée) partait telle quelle chez Brevo,
 * qui répondait 400 `email is not valid in to` — après qu'on ait généré les PDF
 * et créé l'EmailLog pour rien. On valide donc ici, une seule fois, pour le
 * formulaire backoffice et pour l'API.
 */
export const emailRecipientsSchema = z
    .array(z.string().trim())
    .min(1, 'Veuillez saisir au moins un destinataire.')
    .superRefine((recipients, ctx) => {
    const invalid = recipients.filter((recipient) => !z.string().email().safeParse(recipient).success);
    if (invalid.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: invalid.length === 1
                ? `Adresse email invalide : « ${invalid[0]} ».`
                : `Adresses email invalides : ${invalid.map((email) => `« ${email} »`).join(', ')}.`,
        });
    }
});
const supplementalAttachmentSchema = z.object({
    fileId: z.string().min(1),
    fileName: z.string().min(1),
});
/**
 * Corps commun à l'aperçu et à l'envoi d'un mail de dossier : les deux routes
 * construisent exactement le même mail, seul l'envoi effectif les distingue.
 */
export const sendBookingEmailSchema = z.object({
    brevoTemplateId: z.number().int().positive(),
    /**
     * Expéditeur du mail. Absent, on prend celui de l'agence propriétaire du
     * dossier — c'est le cas nominal, le backoffice ne l'envoie que lorsque le
     * conseiller a changé la valeur du select.
     *
     * Liste fermée et non saisie libre : c'est ici la frontière de sécurité. Un
     * `from` arbitraire accepté par l'API ferait du backoffice un relais
     * d'usurpation d'identité, sur un domaine authentifié SPF/DKIM de surcroît.
     */
    senderEmail: z.enum(AGENCY_SENDER_EMAILS).optional(),
    documentTypes: z.array(z.nativeEnum(DocumentType)).optional(),
    recipients: emailRecipientsSchema,
    supplementalAttachments: z.array(supplementalAttachmentSchema).optional(),
    templateParams: z.record(z.unknown()).optional(),
});
